import React from 'react'
import styles from './InvoiceDetails.module.scss'
import Layout from '../../components/core/Layout';
import { useParams } from 'react-router-dom';
import { getTaxRate, postShippingFee, getShippingFee } from '../../apis/invoice-api';
import { INVOICE_TAB_INDEX, InvStatus } from '../../libs/constants';
import { str2number } from '../../libs/helpers';
import { InvoiceMasterData } from "../../apis/models/InvoiceMasterRequest";
import { error, success } from '../../apis/reducers/snackbarsReducer';
import { useDispatch, useSelector } from 'react-redux';
import { getInvoiceMaster, putInvoiceMaster } from '../../apis/invmaster-api';
import { TaxResponseData } from '../../apis/models/TaxResponse';
import DateWithoutTime from '../../libs/dateWithoutTime';
import InvoiceHeader from '../../components/InvoiceDetailsPage/header/InvoiceHeader';
import { RootState } from '../../apis/store';
import { saveMasterInfo } from '../../apis/reducers/invoiceMasterReducer';
import ResetDialog from '../../components/dialogs/confirmDialogs/ResetDialog';
import UploadCsvDialog from '../../components/dialogs/brandList/UploadShippingFeeDialog/UploadCsvDialog';
import ShippingFee, { ShippingFeeTabType } from '../../components/InvoiceDetailsPage/ShippingFee';
import { ShippingFeeResponseData } from '../../apis/models/ShippingFeeResponseData';
import { ShippingFeeRequestItem } from '../../apis/models/ShippingFeeRequest';

const InvoiceShippingFeePage = () => {
  const invoiceMasterState = useSelector((state: RootState) => state.invoiceMaster);
  const dispatch = useDispatch();
  const { id } = useParams();
  const [invoiceMasterData, setInvoiceMasterData] = React.useState<InvoiceMasterData | null>(invoiceMasterState.masterData);
  const [taxItem, setTaxItem] = React.useState<TaxResponseData | null>(null);
  const [shippingFees, setShippingFees] = React.useState<ShippingFeeTabType[]>([]);
  const [notYetSaved, setNotYetSaved] = React.useState<boolean>(false);
  const [openUploadDialog, setOpenUploadDialog] = React.useState<boolean>(false);
  const [openWarningDialog, setOpenWarningDialog] = React.useState<boolean>(false);
  const [isChanged, setIsChanged] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (id) {
      loadAllData();
    }
  },[id])

  const hasError = React.useMemo(() => {
    return shippingFees.some(fee => {
      return (
        fee.error.dateAdded 
        || fee.error.actualShippingFee
        || fee.error.quantity
        || fee.error.unitPrice
      )
    })
  },[shippingFees]);

  const loadAllData = () => {
    getInvoiceMasterData();
    getShippingFeeDetails();
    setNotYetSaved(false);
  }

  const onSaveAndCommit = async () => {
    const isSuccess = await saveShippingFeeDetails();
    if (isSuccess){
      onChangeInvoiceStatus(InvStatus.CONFIRMED)
    }
  }

  const onChangeInvoiceStatus = async (status: InvStatus) => {
    try {
      await putInvoiceMaster(id!, {status: status})
      dispatch(success("請求ステータスを変更しました"));
      loadAllData();
    }catch (err) {
      dispatch(error("請求ステータスの変更に失敗しました"));
    }
  }

  const getInvoiceMasterData = () => {
    getInvoiceMaster(id)
    .then(function (response) {
        setInvoiceMasterData(response.data.data);
        dispatch(saveMasterInfo({masterData: response.data.data}))
        getTaxRate(new Date(response.data.data.from).toString())
          .then(res => setTaxItem(res.data.data))
          .catch(err => console.log(err))
    })
    .catch(function (error) {
        console.log(error);
    })
  }

  const getShippingFeeDetails = () => {
    if (id !== undefined) {
      getShippingFee(id)
        .then((res) => {
          setShippingFees(convertResponseToTabType(res.data.data));
        })
        .catch((err) => {})
    }
  }

  const convertResponseToTabType = (datas: ShippingFeeResponseData[]): ShippingFeeTabType[] => {
    return datas.map((fee: ShippingFeeResponseData) => {
      return {
        uniqueId: fee.shippingItemId,
        isSagawa: fee.yamatoSagawaFlag,
        isChakubarai: fee.payInAdvanceOrArrivalFlag,
        managementNo: fee.managementNo,
        postcode: fee.postcode,
        referenceNo: fee.referenceNo,
        dateAdded: new DateWithoutTime(fee.dateAdded),
        quantity: fee.quantity + "",
        unitPrice: fee.unitPrice + "",
        actualShippingFee: fee.actualShippingFee + "",
        memo: fee.memo,
        error: {},
      } as ShippingFeeTabType;
    })
  }

  const onChangeFees = (items: ShippingFeeTabType[]) => {
    setShippingFees(items);
    setNotYetSaved(true);
  }

  const saveShippingFeeDetails = async (): Promise<boolean> => {
    if (!id) {
      return false
    }
    const req: ShippingFeeRequestItem[] = [];
    shippingFees.forEach(item => {
      if (!item.dateAdded.value) {
        return;
      }
      const data: ShippingFeeRequestItem = {
        invoiceMasterId: id!,
        yamatoSagawaFlag: item.isSagawa,
        payInAdvanceOrArrivalFlag: item.isChakubarai,
        managementNo: item.managementNo ?? "",
        postCode: item.postcode ?? "",
        referenceNo: item.referenceNo,
        dateAdded: item.dateAdded.getValue4Server(),
        quantity: str2number(item.quantity),
        unitPrice: str2number(item.unitPrice),
        actualShippingFee: str2number(item.actualShippingFee),
        memo: item.memo,
      }
      req.push(data);
    })
    try {
      await postShippingFee(id, req)
      dispatch(success("保存しました"));
      loadAllData();

      return true;
    } catch (e) {
      dispatch(error("保存に失敗しました"))
    }
    return false;
  }

  const onClickUpload = () => {
    if (!notYetSaved) {
      setOpenUploadDialog(true);
    } else {
      setOpenWarningDialog(true);
    }
  }

  return (
    <Layout withBreadcrumbs={true} bgColor="#FAFAFA">
      <React.Fragment>
        <div className={styles.Gray}>
          <div>
            <InvoiceHeader
              invoiceMaster={invoiceMasterData ?? undefined}
              tax={taxItem ?? undefined}
              selectedTab={INVOICE_TAB_INDEX.SHIPPING}
              needSave={notYetSaved}
              error={hasError}
              onSaveAndCommit={() => onSaveAndCommit()}
              onChangeStatus={(status) => onChangeInvoiceStatus(status)}
              onMasterChange={() => loadAllData()}
            />
          </div>
          <div  className={styles.tabs}>
            <div className={styles.tabs__body}>
              {invoiceMasterData &&
                <ShippingFee
                  targetInvoice={invoiceMasterData}
                  tabValues={shippingFees}
                  isChanged={isChanged}
                  onNotYetConfirmed={() => onChangeInvoiceStatus(InvStatus.NOT_CONFIRMED)}
                  onReset={() => loadAllData()}
                  onSave={() => saveShippingFeeDetails()}
                  onChange={(items) => {onChangeFees(items); setIsChanged(true)}}
                  onClickUpload={() => onClickUpload()}
                />
              }
            </div>
          </div>
        </div>
        { openWarningDialog &&
          <ResetDialog
            open={openWarningDialog}
            onClose={() => setOpenWarningDialog(false)}  
            onOk={() => {setOpenWarningDialog(false); setOpenUploadDialog(true)}}
          />
        }
        { openUploadDialog &&
          <UploadCsvDialog 
            open={openUploadDialog}
            onClose={() => setOpenUploadDialog(false)}
            onSuccess={() => loadAllData()}
          />
        }
      </React.Fragment>
    </Layout>
  )
}

export default InvoiceShippingFeePage